/**
 * space_lead.ts — THE TRUE INTERCEPT LEAD (space campaign I, 2026-09-30; report §18.2 6th ¶). PURE.
 *
 * The pip used `t = min(2.5, dist / 420)`: the straight-line distance over the bolt speed, clamped past the bolt's
 * 1.6 s life. A crossing, closing or receding target was led by the wrong time, and beyond ~670 m the pip promised a
 * hit no bolt could reach. The bolt inherits the shooter's FULL velocity (`fire()`: `fwd·420 + ship.vel`), so in the
 * shooter's frame it flies at exactly `s` along the nose and the intercept is the smallest positive root of
 *
 *     (v·v − s²)·t² + 2(r·v)·t + r·r = 0        r = target − shooter, v = target.vel − shooter.vel
 *
 * solved in the cancellation-free form (q = −½(b + sgn(b)·√disc); the roots are q/a and c/q). a = 0 (the target's
 * relative speed equals the bolt's) is the linear case; no positive root = the target outruns the bolt. A solution past
 * the bolt's life is "beyond" — drawn as a ghost "out of reach" mark, never as a pip that promises a hit.
 *
 * CONFIDENCE: the constant-velocity model misses an accelerating target by ≈ ½|a⊥|t² (a⊥ = the acceleration across the
 * line of fire); an old velocity sample adds |a⊥|·τ·t. That miss is judged against the TARGET'S OWN hit radius (a
 * fighter's 9 m and a frigate's 55 m are not one threshold). A jammed pilot's lead is computed from a DELAYED velocity
 * sample and is always low (a presentation impairment — §21.10: a modified client could recompute it; the enforceable
 * EW effect is the server's metered rate). The crosshair touching a mark never guarantees a hit: the bolt still flies.
 */

export interface V3 { x: number; y: number; z: number }

/** The player's twin bolts — MIRROR of SpaceModule's `BOLT_SPEED` / `BOLT_LIFE_S` (verify:space-flight pins the text). */
export const BOLT_SPEED_MPS = 420;
export const BOLT_LIFE_S = 1.6;
/** Confidence bands: the predicted miss over the target's hit radius. */
export const CONF_HIGH_RATIO = 0.5;
export const CONF_MED_RATIO = 1.5;
/** A velocity sample older than this is at most "medium"; older than the second, "low". */
export const SAMPLE_STALE_S = 0.35;
export const SAMPLE_OLD_S = 1.0;
/** A jammed pilot's lead reads a velocity this old (a delayed track). */
export const JAM_DELAY_MS = 800;

const dot = (a: V3, b: V3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const len = (a: V3): number => Math.sqrt(dot(a, a));

/** The smallest positive intercept time, or null when no positive root exists (the bolt can never reach). */
export function interceptTime(r: V3, v: V3, s: number): number | null {
  if (!(s > 0) || ![r.x, r.y, r.z, v.x, v.y, v.z].every(Number.isFinite)) return null;
  const c = dot(r, r);
  if (c === 0) return 0;
  const a = dot(v, v) - s * s;
  const b = 2 * dot(r, v);
  if (a === 0) { // the linear case: b·t + c = 0 — only a closing target is caught
    if (!(b < 0)) return null;
    const t = -c / b;
    return t > 0 && Number.isFinite(t) ? t : null;
  }
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const sq = Math.sqrt(disc);
  const q = -0.5 * (b + (b >= 0 ? sq : -sq));
  let best: number | null = null;
  for (const t of [q / a, q !== 0 ? c / q : Number.NaN]) {
    if (Number.isFinite(t) && t > 0 && (best === null || t < best)) best = t;
  }
  return best;
}

export type LeadKind = 'lead' | 'beyond' | 'none';
export type LeadConfidence = 'high' | 'medium' | 'low';
export interface LeadInput {
  r: V3;                 // target − shooter (m)
  v: V3;                 // target velocity − shooter velocity (m/s) — the bolt inherits the shooter's velocity
  s?: number;            // bolt speed in the shooter's frame (420)
  life?: number;         // bolt life (1.6 s)
  accel?: V3 | null;     // the target's measured acceleration (m/s²)
  sampleAgeS?: number;   // how old the target's velocity sample is
  hitRadius?: number;    // the target's own hit radius (m)
  jammed?: boolean;      // the shooter's sensors are jammed (the lead is delayed and low)
}
export interface LeadSolution {
  kind: LeadKind;
  t: number;             // intercept time (s); 0 for 'none'
  aim: V3;               // the aim point relative to the shooter: r + v·t (the pip sits at shooter + aim)
  confidence: LeadConfidence;
  missM: number;         // predicted miss of the constant-velocity model (m)
  reasons: string[];     // why the confidence is not high
}

/** Solve the lead and judge how far to trust it. */
export function solveLead(inp: LeadInput): LeadSolution {
  const s = inp.s ?? BOLT_SPEED_MPS, life = inp.life ?? BOLT_LIFE_S;
  const t = interceptTime(inp.r, inp.v, s);
  if (t === null) return { kind: 'none', t: 0, aim: { ...inp.r }, confidence: 'low', missM: Infinity, reasons: ['the target outruns the bolt'] };
  const aim = { x: inp.r.x + inp.v.x * t, y: inp.r.y + inp.v.y * t, z: inp.r.z + inp.v.z * t };
  const kind: LeadKind = t <= life ? 'lead' : 'beyond';
  const reasons: string[] = [];
  // The acceleration ACROSS the line of fire is what moves the target off the predicted point.
  let aPerp = 0;
  if (inp.accel) {
    const al = len(aim);
    const u = al > 1e-9 ? { x: aim.x / al, y: aim.y / al, z: aim.z / al } : { x: 0, y: 0, z: 0 };
    const ad = dot(inp.accel, u);
    aPerp = len({ x: inp.accel.x - u.x * ad, y: inp.accel.y - u.y * ad, z: inp.accel.z - u.z * ad });
  }
  const age = Math.max(0, inp.sampleAgeS ?? 0);
  const missM = 0.5 * aPerp * t * t + aPerp * age * t;
  const R = Math.max(1, inp.hitRadius ?? 9);
  const ratio = missM / R;
  let conf: LeadConfidence = ratio <= CONF_HIGH_RATIO ? 'high' : ratio <= CONF_MED_RATIO ? 'medium' : 'low';
  if (ratio > CONF_HIGH_RATIO) reasons.push('the target is manoeuvring');
  if (age > SAMPLE_OLD_S) { conf = 'low'; reasons.push(`its track is ${age.toFixed(1)} s old`); }
  else if (age > SAMPLE_STALE_S) { if (conf === 'high') conf = 'medium'; reasons.push(`its track is ${age.toFixed(1)} s old`); }
  if (inp.jammed) { conf = 'low'; reasons.push('your sensors are jammed — the lead is delayed'); }
  if (kind === 'beyond') reasons.push(`out of reach — the bolt lives ${life} s`);
  return { kind, t, aim, confidence: conf, missM, reasons };
}

/** The HUD's one-line reading of a solution: a glyph + words (never colour alone). */
export function leadLabel(sol: LeadSolution): string {
  if (sol.kind === 'none') return '✕ no intercept — it outruns your bolts';
  if (sol.kind === 'beyond') return `⊘ out of reach · ${sol.t.toFixed(1)} s`;
  if (sol.confidence === 'high') return '';
  // Short words beside a moving bracket (the long reasons stay in `reasons`); the WORST cause names itself.
  const r = sol.reasons;
  const why = r.some((x) => /jammed/.test(x)) ? 'jammed, delayed'
    : r.some((x) => /track is/.test(x)) ? (r.find((x) => /track is/.test(x)) ?? '').replace(/^its track is /, 'track ')
      : r.some((x) => /manoeuvring/.test(x)) ? 'manoeuvring' : '';
  return `${sol.confidence === 'medium' ? '≈' : '≋'} ${sol.confidence} lead${why ? ` · ${why}` : ''}`;
}

// ── The track: a short velocity history per target (acceleration + the delayed sample a jam shows) ──────────────────
interface Track { samples: Array<{ ms: number; v: V3 }>; a: V3; seen: number }
export class LeadTracker {
  private tracks = new Map<string, Track>();
  constructor(private readonly keepMs = 2000, private readonly accelWindowMs = 150, private readonly accelEma = 0.25) {}

  /** Record the target's velocity at `ms` (wall or performance clock — one clock per tracker). */
  observe(key: string, ms: number, v: V3): void {
    let tr = this.tracks.get(key);
    if (!tr) { tr = { samples: [], a: { x: 0, y: 0, z: 0 }, seen: ms }; this.tracks.set(key, tr); }
    if (![v.x, v.y, v.z, ms].every(Number.isFinite)) return;
    tr.seen = ms;
    tr.samples.push({ ms, v: { x: v.x, y: v.y, z: v.z } });
    while (tr.samples.length > 2 && ms - tr.samples[0].ms > this.keepMs) tr.samples.shift();
    // Acceleration over at least the window (a one-tick finite difference is noise).
    let old: { ms: number; v: V3 } | null = null;
    for (let i = tr.samples.length - 1; i >= 0; i--) { if (ms - tr.samples[i].ms >= this.accelWindowMs) { old = tr.samples[i]; break; } }
    if (old) {
      const dt = (ms - old.ms) / 1000;
      const raw = { x: (v.x - old.v.x) / dt, y: (v.y - old.v.y) / dt, z: (v.z - old.v.z) / dt };
      const k = this.accelEma;
      tr.a = { x: tr.a.x + (raw.x - tr.a.x) * k, y: tr.a.y + (raw.y - tr.a.y) * k, z: tr.a.z + (raw.z - tr.a.z) * k };
    }
  }
  accel(key: string): V3 | null { return this.tracks.get(key)?.a ?? null; }
  /** The velocity the track held `delayMs` ago (a jammed pilot's lead), or the oldest it has. */
  delayed(key: string, nowMs: number, delayMs: number): V3 | null {
    const tr = this.tracks.get(key);
    if (!tr || !tr.samples.length) return null;
    const want = nowMs - delayMs;
    let pick = tr.samples[0];
    for (const smp of tr.samples) { if (smp.ms <= want) pick = smp; else break; }
    return pick.v;
  }
  /** Forget targets not seen for a while. */
  prune(nowMs: number): void { for (const [k, tr] of this.tracks) if (nowMs - tr.seen > this.keepMs * 2) this.tracks.delete(k); }
  size(): number { return this.tracks.size; }
}
